import { cn } from "../../lib/utils";
import { CalenderIcon } from "asset/icons";
import classNames from "classnames";
import { EMAIL_REGEX } from "constants/global";
import dayjs from "dayjs";
import { useRef } from "react";
import { formatMoney } from "utilities/formatMoney";

/**
 * @param {Object} props
 * @param {function(string):void} props.onChange
 * @param {string} [props.value]
 * @param {("text"|"password"|"email"|"number"|"tel"|"url"|"search"|"date"|"time"|"checkbox"|"radio"|'textarea')} [props.type]
 * @param {boolean} [props.required]
 * @param {string} [props.label]
 * @param {string} [props.labelClassName]
 * @param {string} [props.className]
 * @param {string} [props.inputClassName]
 * @param {string} [props.placeholder]
 * @param {("integer"|"decimal")} [props.formatType]
 * @param {string} [props.unit]
 * @param {string} [props.unitClassName]
 * @param {string} [props.unitWrapperClassName]
 * @param {string} [props.max]  date max type eg: 2018-12-31
 * @param {string} [props.min]  date min type eg: 2018-12-31
 */
const Input = ({
    onChange,
    value,
    type = "text",
    required = true,
    label,
    labelClassName,
    className,
    placeholder,
    maxLength,
    formatType,
    unit,
    unitClassName,
    unitWrapperClassName,
    max,
    min,
    disabled = false,
    inputClassName,
    step,
    name = "",
    inputMode,
    pattern,
    error,
}) => {
    const classes = classNames(
        `bg-[#f1f5f9] h-[56px] rounded-lg text-main-dark w-full !px-4 placeholder:font-light placeholder:text-sm placeholder:text-main-grey !shadow-[inset_0_0_0_1000px_#f1f5f9]`,
        className ? className : "!mt-2",
        error && !disabled && "!bg-[#FFF1F3] !shadow-[inset_0_0_0_1000px_#FFF1F3] !ring-1 !ring-[#F04438]",
        disabled && "!bg-[#F8FAFC] !text-[#98A2B3] !shadow-[inset_0_0_0_1000px_#F8FAFC] disabled:opacity-100 disabled:[-webkit-text-fill-color:#98A2B3]",
        inputClassName
    );

    const inputs = () => (
        <>
            {type === "number" ? (
                <NumInput
                    required={required}
                    placeholder={placeholder}
                    className={classes}
                    onChange={onChange}
                    value={value}
                    formatType={formatType}
                    max={max}
                    step={step}
                    min={min}
                    maxLength={maxLength}
                    disabled={disabled}
                    name={name}
                />
            ) : type === "textarea" ? (
                <textarea
                    className={`${classes} resize-none !h-[96px] !py-2.5`}
                    required={required}
                    placeholder={placeholder}
                    onChange={(evt) => onChange(evt.target.value)}
                    onKeyUp={(evt) => evt.preventDefault()}
                    value={value ?? ""}
                    disabled={disabled}
                    name={name}
                />
            ) : type === "date" ? (
                <DateInput
                    className={classes}
                    max={max}
                    min={min}
                    onChange={onChange}
                    placeholder={placeholder}
                    required={required}
                    value={value}
                    disabled={disabled}
                    name={name}
                />
            ) : (
                <input
                    required={required}
                    placeholder={placeholder}
                    className={classes}
                    onChange={(evt) => onChange(evt.target.value)}
                    onKeyUp={(evt) => evt.preventDefault()}
                    maxLength={maxLength}
                    type={type}
                    value={value ?? ""}
                    max={max}
                    name={name}
                    disabled={disabled}
                    inputMode={inputMode}
                    pattern={pattern}
                />
            )}
        </>
    );

    return (
        <>
            {label && (
                <p
                    className={cn(
                        "mb-2 font-medium text-sm text-left text-[#475467]",
                        labelClassName
                    )}
                >
                    {label}
                </p>
            )}
            {unit ? (
                <div
                    className={cn(
                        "flex h-[56px] items-center rounded-lg bg-[#f1f5f9]",
                        disabled && "bg-[#F8FAFC] text-[#98A2B3]",
                        unitWrapperClassName ? unitWrapperClassName : "mt-2"
                    )}
                >
                    {inputs()}

                    {typeof unit === "object" ? (
                        unit
                    ) : (
                        <p
                            className={`!pl-0.5 !pr-3.5 font-medium text-sm text-main ${unitClassName}`}
                        >
                            {unit}
                        </p>
                    )}
                </div>
            ) : (
                inputs()
            )}
            {error && <p className="mt-1 text-xs text-[#D92D20]">{error}</p>}
        </>
    );
};

const NumInput = ({
    required,
    onChange,
    placeholder,
    className,
    value,
    formatType,
    max = Infinity,
    maxLength,
    min = 0,
    step,
    disabled,
    name,
}) => (
    <input
        className={className}
        required={required}
        placeholder={placeholder}
        maxLength={maxLength}
        onKeyUp={(evt) => evt.preventDefault()}
        step={step}
        value={formatType ? (value ? formatMoney(value) : "") : value ?? ""}
        disabled={disabled}
        name={name}
        onChange={(evt) => {
            const value = evt.target.value.replace(/[,\s]/g, "");
            if (+value > max || +value < min) return;
            if (formatType === "decimal") {
                if (value.includes(".")) {
                    // replace(/\D/g, '')
                    const [wholeNum, decimalNum] = value.split(".");
                    //                 !isNaN(+valuesSplit[0]) && !isNaN(+valuesSplit[1]) && onChange(value);
                } else {
                    !isNaN(+value) && onChange(value);
                }
            } else {
                !isNaN(+value) && onChange(value);
            }
        }}
    />
);

const DateInput = ({ placeholder, value, required, onChange, min, max, className, disabled, name }) => {
    const dateRef = useRef();

    return (
        <button
            onClick={() => {
                if (!disabled) {
                    if (dateRef.current?.showPicker) dateRef.current.showPicker();
                    else {
                        dateRef.current?.focus();
                        dateRef.current?.click?.();
                    }
                }
            }}
            className={`bg-[#f1f5f9] relative flex w-full items-center justify-between rounded-lg h-[56px] !px-4 ${
                value ? "text-main-dark" : "text-main-grey text-sm"
            } ${className}`}
            type="button"
            disabled={disabled} // Disable the button if the field is disabled
        >
            {value ? dayjs(value).format("MMM D, YYYY") : placeholder}
            <CalenderIcon />
            <input
                ref={dateRef}
                type="date"
                name={name || placeholder}
                value={value}
                min={min}
                max={max}
                onChange={(evt) => onChange(evt.target.value)}
                className="absolute inset-0 z-10 w-full h-full opacity-0 cursor-pointer [appearance:none]"
                required={value ? false : required}
                disabled={disabled} // Ensure the input is disabled when required
            />
        </button>
    );
};

export default Input;
